import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useInterval } from '../../hooks/timer';
import { Utilities } from '@gscope-mfe/app-bridge';
import sortedIndexBy from 'lodash/sortedIndexBy';
import noop from 'lodash/noop';
import createPersistedState from 'use-persisted-state';
import NOTIFICATION_CONFIG from './config';
const { setUserPreference } = Utilities;
export function getNotifications(modsWithNotifications, state) {
    return Promise.all(modsWithNotifications.map((mod) => mod.fetcher(state))).then(
        (data) => {
            return data
                .reduce((acc, c, idx) => {
                    acc.push(
                        ...c.map((n) =>
                            Object.assign(
                                n,
                                {
                                    nId: `${modsWithNotifications[idx].name}_${n.nId}`,
                                    _nId: n.nId,
                                },
                                {
                                    mod: modsWithNotifications[idx],
                                },
                            ),
                        ),
                    );
                    return acc;
                }, [])
                .sort((a, b) => b.timestamp - a.timestamp);
        },
        () => [],
    );
}

const calculateCount = (ns, lastNotificationTime) =>
    Math.max(
        0,
        sortedIndexBy(
            ns.map((n) => n.timestamp),
            lastNotificationTime,
            (i) => -i,
        ),
    );

const useLastNotification = createPersistedState(NOTIFICATION_CONFIG.storageKey);
export default function useNotifications(modsWithNotifications, notificationPreferences, onUpdate = noop) {
    const [error, setError] = useState(null);

    if (error) {
        throw error;
    }

    const mounted = useRef();
    const onUpdateRef = useRef();
    const [lastNotification, setLastNotification] = useLastNotification(notificationPreferences.lastNotification);
    const lastNotificationRef = useRef(lastNotification);
    lastNotificationRef.current = lastNotification;
    const [notifications, setNotifications] = useState([]);
    const count = useMemo(
        () => calculateCount(notifications, lastNotificationRef.current.time),
        [notifications, lastNotificationRef.current.time],
    );
    const asyncNotificationConfigRef = useRef({
        maxNotificationsInPopover: 5,
        maxNotificationsInDrawer: 30,
        interval: 15000,
    });

    const setNotificationsAndTime = (ns) => {
        if (!ns.length || !mounted.current) {
            return;
        }

        let lastNotificationIdx = calculateCount(ns, lastNotificationRef.current.time);
        markNotificationsAsSeen(ns, lastNotificationIdx);
    };

    const updateNotifications = function (ns, nextLastNotification) {
        lastNotificationRef.current = nextLastNotification;
        setLastNotification(nextLastNotification);
        setNotifications(ns);
        onUpdateRef.current();
    };

    const markNotificationsAsSeen = (ns, start = 0, end = ns.length) => {
        try {
            if (!ns.length) {
                return;
            } // happens when all the notifications are unviewed

            if (start >= ns.length) {
                updateNotifications(ns, lastNotificationRef.current);
                return;
            }

            for (let i = start; i < end; i++) {
                ns[i].seen = true;
            }

            const nextLastNotification = {
                time: ns[start].timestamp,
            };
            setUserPreference({
                pageName: 'notificationPreferences',
                key: 'lastNotification',
                value: nextLastNotification,
            }).then(() => updateNotifications(ns, nextLastNotification));
        } catch (error) {
            setError(error);
        }
    };

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    });
    useEffect(() => {
        mounted.current = true;
        modsWithNotifications.forEach((mod) => mod.init(notificationPreferences));
        getNotifications(modsWithNotifications)
            .then(setNotificationsAndTime)
            .then(() => modsWithNotifications.forEach((mod) => mod.onSync()));
        NOTIFICATION_CONFIG.asyncValues().then(({ data }) => (asyncNotificationConfigRef.current = data));
        return () => {
            mounted.current = false;
        };
    }, []);
    useInterval(() => {
        getNotifications(modsWithNotifications)
            .then(setNotificationsAndTime)
            .then(() => modsWithNotifications.forEach((mod) => mod.onSync()));
    }, asyncNotificationConfigRef.current.interval);
    return {
        count,
        notifications,
        markNotificationsAsSeen,
        ...asyncNotificationConfigRef.current,
    };
}
